type DiagnosticFields = {
  code?: string;
  detail?: string;
  hint?: string;
  constraint?: string;
  table?: string;
  column?: string;
  routine?: string;
  severity?: string;
};

type RpcErrorBody = {
  error: string;
  code?: string;
  retryAfterMs?: number;
  details?: Record<string, unknown>;
  diagnostics?: DiagnosticFields & { cause?: DiagnosticFields };
};

const DIAGNOSTIC_FIELD_NAMES = [
  "code",
  "detail",
  "hint",
  "constraint",
  "table",
  "column",
  "routine",
  "severity",
] as const;

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : undefined;
}

function redactDiagnosticValue(value: string): string {
  return value
    .replace(/postgres(?:ql)?:\/\/[^\s"']+/gi, "postgres://[redacted]")
    .replace(/\b(password|token|secret)=([^&\s]+)/gi, "$1=[redacted]");
}

function collectDiagnosticFields(value: unknown): DiagnosticFields {
  const record = asRecord(value);
  if (!record) {
    return {};
  }

  const result: DiagnosticFields = {};
  for (const fieldName of DIAGNOSTIC_FIELD_NAMES) {
    const fieldValue = record[fieldName];
    if (typeof fieldValue === "string" && fieldValue.length > 0) {
      result[fieldName] = redactDiagnosticValue(fieldValue);
    }
  }
  return result;
}

function hasDiagnosticFields(fields: DiagnosticFields): boolean {
  return Object.keys(fields).length > 0;
}

function hasNonCodeDiagnosticFields(fields: DiagnosticFields): boolean {
  return Object.keys(fields).some((key) => key !== "code");
}

function isJobAdmissionCode(code: unknown): boolean {
  return typeof code === "string" && code.startsWith("job_");
}

function sanitizeRpcDetailValue(value: unknown, depth = 0): unknown {
  if (value == null || typeof value === "number" || typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return redactDiagnosticValue(value);
  }
  if (depth >= 2) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeRpcDetailValue(item, depth + 1));
  }
  const record = asRecord(value);
  if (!record) {
    return undefined;
  }
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(record).slice(0, 20)) {
    const sanitized = sanitizeRpcDetailValue(item, depth + 1);
    if (sanitized !== undefined) {
      result[key] = sanitized;
    }
  }
  return result;
}

function collectJobAdmissionDetails(
  record: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const sanitized = sanitizeRpcDetailValue(record?.details);
  return sanitized && typeof sanitized === "object" && !Array.isArray(sanitized)
    ? (sanitized as Record<string, unknown>)
    : undefined;
}

export function buildRpcErrorBody(error: unknown, messageOverride?: string): RpcErrorBody {
  const record = asRecord(error);
  const message =
    messageOverride ??
    (error instanceof Error ? error.message : typeof error === "string" ? error : "Unknown error");
  const body: RpcErrorBody = { error: message };

  if (typeof record?.code === "string" && record.code.length > 0) {
    body.code = record.code;
  }

  if (isJobAdmissionCode(body.code)) {
    if (typeof record?.retryAfterMs === "number" && Number.isFinite(record.retryAfterMs)) {
      body.retryAfterMs = Math.max(0, Math.trunc(record.retryAfterMs));
    }
    const details = collectJobAdmissionDetails(record);
    if (details) {
      body.details = details;
    }
  }

  const diagnostics: DiagnosticFields & { cause?: DiagnosticFields } = collectDiagnosticFields(error);
  const causeDiagnostics = collectDiagnosticFields(record?.cause);
  if (hasDiagnosticFields(causeDiagnostics)) {
    diagnostics.cause = causeDiagnostics;
  }
  if (hasNonCodeDiagnosticFields(diagnostics) || diagnostics.cause) {
    body.diagnostics = diagnostics;
  }

  return body;
}
